import _ from 'lodash';
import moment from 'moment';

/**
 * Max span on the calendar: inclusive "Da" … "A" can cover at most this many dates
 * (e.g. 8 → 23/03 … 30/03). Clamp uses exclusive end = start + this many calendar days
 * so the last day is fully included (Loki end is first instant *after* "A", not 00:00 *of* "A").
 */
export const gatewayLogsMaxCalendarDays = 8;

/**
 * Local midnight at the start of the calendar day of `ms` (wall date from pickers, not UTC day).
 * Avoids UTC-midnight picker values shifting "day" vs Loki (which uses Unix ns = UTC).
 */
export function localCalendarDayStartMs(ms: number): number {
  const m = moment(ms);
  const key = m.format('YYYY-MM-DD');
  return moment(key, 'YYYY-MM-DD', true).valueOf();
}

/**
 * Loki query_range uses [start, end) with end exclusive: first instant *after* the last selected local day
 * (i.e. 00:00 on the day *after* "A" in local time — not 00:00 *on* "A", which would cut off that whole day).
 */
export function localCalendarDayEndExclusiveMs(ms: number): number {
  const m = moment(ms);
  const key = m.format('YYYY-MM-DD');
  return moment(key, 'YYYY-MM-DD', true).add(1, 'day').valueOf();
}

export function clampGatewayLogsRangeMs(startMs: number, endMs: number): { startMs: number; endMs: number } {
  let s = Math.min(startMs, endMs);
  let e = Math.max(startMs, endMs);
  const maxEndExclusive = moment(localCalendarDayStartMs(s)).add(gatewayLogsMaxCalendarDays, 'day').valueOf();
  if (e > maxEndExclusive) {
    e = maxEndExclusive;
  }
  return { startMs: s, endMs: e };
}

export function msToNsString(ms: number): string {
  return String(BigInt(Math.trunc(ms)) * 1000000n);
}

/** Default Loki window: last ~7 days until now (rolling; independent of max calendar clamp). */
const defaultRollingDays = 7;

export function buildDefaultGatewayLogsParams(): { startNs: string; endNs: string } {
  const endMs = Date.now();
  const startMs = moment(endMs).subtract(defaultRollingDays, 'days').valueOf();
  return { startNs: msToNsString(startMs), endNs: msToNsString(endMs) };
}

export function parseNsStringToMs(nsStr: string): number {
  return Number(BigInt(nsStr) / 1000000n);
}

export function normalizeGatewayLogsParams(
  startNs: string | undefined,
  endNs: string | undefined,
): { startNs: string; endNs: string } {
  if (_.isString(startNs) && startNs.length > 0 && _.isString(endNs) && endNs.length > 0) {
    const sMs = parseNsStringToMs(startNs);
    const eMs = parseNsStringToMs(endNs);
    const c = clampGatewayLogsRangeMs(sMs, eMs);
    return { startNs: msToNsString(c.startMs), endNs: msToNsString(c.endMs) };
  }
  return buildDefaultGatewayLogsParams();
}

/** Drop log lines whose raw line matches `updater` (case-insensitive). Mirrors Loki line filter when backend ignores excludeUpdater. */
export function filterGatewayLogEntriesExcludeUpdater<T extends { line: string }>(entries: T[]): T[] {
  return _.filter(entries, (e) => !/updater/i.test(e.line || ''));
}
